import React, { Component } from 'react';
import componentStyles from 'styles/partials/_file-selector.css';
import inputStyles from 'styles/partials/_baseinput.css';
import { appActions } from 'state/modules/app';
import * as entities from 'graphql/entities';
import Dropdown from './Dropdown';
import QueryDropdown from 'components/connectedForm/QueryDropdown';
import getValue from '../connectedForm/utils/getValue';
import { default as Form } from './index';
import BlurTextField from './BlurTextField';
import ZipImageSelector from './ZipFileSelector';
import componentMutation from '../../utils/ComponentMutation';
import ProductBulkImageProcessMutation from 'graphql/mutations/product/ProductBulkImageProcessMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import { DetailsList, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { mdiFileImageOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { ResourceType } from '../../graphql/schema/p4types';

const styles = Object.assign({}, componentStyles, inputStyles);

const ImageSelectCard = (props) => {
  const stopPropagation = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleImageDrop = (event) => {
    event.preventDefault();
    props.handleImageFiles(event.dataTransfer.files);
  };

  // Allow click to change image if blank
  const handleImageClick = (event) => {
    event.preventDefault();
    props.handleEditClick();
  };

  const { preview, tabIndex } = props;
  const imageSource = preview.url || `/api/resources/image/default-no-image.png`;

  const image = <img src={imageSource} style={{ maxHeight: '100%' }} alt={'No Image Available'} />;

  if (preview.match) {
    return (
      <div className={`${styles.frame} ${styles.inputControl}`} tabIndex={tabIndex}>
        {image}
      </div>
    );
  }

  return (
    <div
      className={`${styles.frame} ${styles.inputControl}`}
      onDragEnter={(e) => stopPropagation(e)}
      onDragOver={(e) => stopPropagation(e)}
      onDragExit={(e) => stopPropagation(e)}
      onDrop={handleImageDrop}
      onClick={handleImageClick}
      ref={props.imageInputRef}
      tabIndex={tabIndex}
    >
      {image}
    </div>
  );
};

/**
 * Bulk Image Selection Component.
 *
 * Currently this is very closely tied into 'Products' bulk image import.
 * Some work required to uncouple them if required for re-use elsewhere.
 */
export default class BulkImageSelector extends Component {
  constructor(props) {
    super(props);
    this.getErrorMessage = this.getErrorMessage.bind(this);
    this.handleEditClick = this.handleEditClick.bind(this);
    this.handleImageRemoval = this.handleImageRemoval.bind(this);
    this.handleFilesUpload = this.handleFilesUpload.bind(this);
    this.handleFilesUploadSuccess = this.handleFilesUploadSuccess.bind(this);
    this.handleImageSelect = this.handleImageSelect.bind(this);
    this.handleImageFiles = this.handleImageFiles.bind(this);
    this.onChangeItem = this.onChangeItem.bind(this);
    this.onUploadSuccess = this.onUploadSuccess.bind(this);
    this.styles = {};
    Object.assign(this.styles, componentStyles, inputStyles);

    this.imageInputRef = React.createRef();
    this.fileSelectorRef = React.createRef();

    this.columns = [
      { key: 'column1', name: 'Icon', fieldName: 'icon', minWidth: 30, maxWidth: 60, isResizable: true },
      { key: 'column2', name: 'File Name', fieldName: 'filename', minWidth: 100, maxWidth: 200, isResizable: true },
    ];

    this.state = {
      unmatched: [],
      successfulMatches: [],
      matched: [],
      matchedImageListItems: [],
      unmatchedImageListItems: [],
    };
  }

  focus() {
    this.imageInputRef.current.focus();
  }

  handleImageFiles(files) {
    const newFiles = Array.from(files).map((file) => {
      return {
        name: file.name, //Required for matching on re-uploads.
        file: file,
        url: URL.createObjectURL(file), //Local URL for displaying preview of files.
      };
    });

    this.setState({
      unmatched: [...this.state.unmatched, ...newFiles],
    });
    this.handleFilesUpload(files);
  }

  handleImageSelect(event) {
    event.preventDefault();
    this.handleImageFiles(event.target.files);
  }

  handleFilesUploadSuccess(response) {
    if (response.response.successful) {
      const matches = response.response.successfulMatches;

      //Read through the unmatched and separate the ones that have been matched to a Product.
      const matched = this.state.matched;
      const unmatched = [];
      this.state.unmatched.forEach((preview) => {
        const match = matches.find((match) => match[preview.name]);

        if (match) {
          matched.push({
            ...preview,
            match: match[preview.name],
          });
          return;
        }
        unmatched.push(preview);
      });
      this.setState(
        {
          unmatched: unmatched,
          matched: matched,
        },
        () => {
          this.props.onChange(matched.map((preview) => preview.match));
        }
      );
    }
  }

  handleFilesUpload(files) {
    if (this.props.readOnly) return;
    let size = 0;
    for (let i = 0; i < files.length; i++) {
      size += files[i].size;

      //Either at the last file, or hit the post limit - Post this chunk before
      // continuing.
      if (i === files.length - 1 || size > 10000000) {
        size = 0;
        const formData = new FormData();
        Promise.all(
          [].map.call(files, (file) => {
            return new Promise((resolve) => {
              const fr = new FileReader();
              fr.onloadend = () => {
                resolve({ result: fr.result, file: file });
              };
              fr.readAsDataURL(file);
            });
          })
        ).then((results) => {
          results.forEach((result) => {
            formData.append(`file`, result.file, result.file.name);
          });

          this.props.dispatch(appActions.uploadImages(formData)).then(this.handleFilesUploadSuccess);
        });
      }
    }
  }

  handleImageRemoval(event, index) {
    event.preventDefault();
    const newPreviews = [...this.state.unmatched];
    newPreviews.splice(index, 1);

    this.setState(
      {
        unmatched: newPreviews,
      },
      () => {
        if (this.fileSelectorRef && this.fileSelectorRef.current) {
          this.fileSelectorRef.current.value = '';
        }
      }
    );
  }

  getErrorMessage(message) {
    return <div className={this.styles.errorMessage}>{message[0].message}</div>;
  }

  handleEditClick() {
    const clickEvent = new MouseEvent('click', {
      view: window,
      bubbles: false,
      cancelable: false,
    });
    this.fileSelectorRef.current.dispatchEvent(clickEvent);
  }

  onChangeItem(value, index) {
    const result = this.state.unmatched[index];
    const fileExtension = result.name.slice(((result.name.lastIndexOf('.') - 1) >>> 0) + 2);
    const newValue = getValue(value);
    const newFileName = `${newValue.reference}.${fileExtension}`; //Rename it to match the target product.

    const formData = new FormData();

    const newPreviews = [...this.state.unmatched];
    newPreviews[index].name = newFileName;

    const fr = new FileReader();
    fr.onloadend = () => {
      //Append the file with a modified name this time.
      formData.append(`file`, result.file, newFileName);
      this.props.dispatch(appActions.uploadImages(formData)).then(this.handleFilesUploadSuccess);
    };
    fr.readAsDataURL(result.file);
  }

  renderImages(images, colOverride) {
    return images.map((preview, index) => {
      let productSelect, removeLink;
      if (!preview.match && preview.url) {
        removeLink = (
          <div className={this.styles.removeLink} onClick={(event) => this.handleImageRemoval(event, index)}>
            <span className="icon">delete</span>
            <span> Remove Image</span>
          </div>
        );
        productSelect = (
          <QueryDropdown
            label={'Product'}
            {...entities.Product.dropdown}
            component={Dropdown}
            onChange={(value) => this.onChangeItem(value, index)}
          />
        );
      }

      const Card = this.props.card;

      return (
        <Form.Fieldset key={`_${index}`} cols={colOverride || 1}>
          {!!preview.file.name ? (
            <BlurTextField value={preview.file.name} label={'Images'} readOnly={true} type="text" />
          ) : (
            <BlurTextField
              value={''}
              label={'Images'}
              readOnly={true}
              type={'text'}
              hint={'Click the Image below or Drag + Drop files to the image to start.'}
            />
          )}
          {productSelect}
          <Card index={index} entity={preview.match} label="Card" />
          <Form.Field
            component={ImageSelectCard}
            preview={preview}
            tabIndex={this.props.tabIndex}
            handleEditClick={this.handleEditClick}
            handleImageFiles={this.handleImageFiles}
            imageInputRef={this.imageInputRef}
          />
          {removeLink}
        </Form.Fieldset>
      );
    });
  }

  onUploadSuccess(filename) {
    const variables = { clientMutationId: getClientMutationId(), filename };
    componentMutation(ProductBulkImageProcessMutation, variables, true).then(({ response, errors }) => {
      if (errors) {
        return handleComponentQueryError(
          {
            data: response,
            errors,
          },
          this.props
        );
      }

      const matchedImages = response.productBulkImageProcess.matchedImages;
      const matchedImageListItems = [];
      for (const matchedImage of matchedImages) {
        matchedImageListItems.push({
          key: matchedImage,
          icon: <Icon path={mdiFileImageOutline} size={1} />,
          filename: matchedImage,
        });
      }

      const unmatchedImages = response.productBulkImageProcess.unmatchedImages;
      const unmatchedImageListItems = [];
      for (const unmatchedImage of unmatchedImages) {
        unmatchedImageListItems.push({
          key: unmatchedImage,
          icon: <Icon path={mdiFileImageOutline} size={1} />,
          filename: unmatchedImage,
        });
      }

      this.setState({
        matchedImageListItems,
        unmatchedImageListItems,
      });
    });
  }

  render() {
    const { message, readOnly } = this.props;

    let styleName = '';
    let errorMessage = '';
    if (message !== undefined && message.length > 0) {
      styleName = 'invalid';
      errorMessage = this.getErrorMessage(message);
    }

    const unmatched = this.state.unmatched.length > 0 ? this.state.unmatched : [{ url: null, file: { name: '' } }];
    const images = this.renderImages(unmatched);
    const matchedImages = this.renderImages(this.state.matched, 1 / 3);

    return (
      <Form.TabPanel formName={'Bulk Image Upload'}>
        <Form.TabContent id="main" label="New Images" badge={this.state.unmatched.length} tabStyle={{ width: '200px' }}>
          <div className={`${this.styles.inputGroup} ${this.styles[styleName]}`}>
            {errorMessage}
            <input
              name="file"
              type="file"
              onChange={this.handleImageSelect}
              style={{ display: 'none' }}
              ref={this.fileSelectorRef}
              accept="image/*"
              disabled={readOnly}
              multiple={true}
            />
            <Form.Row>{images}</Form.Row>
          </div>
        </Form.TabContent>
        <Form.TabContent
          id="matched"
          label="Successful Uploads"
          badge={matchedImages.length}
          type="info"
          tabStyle={{ width: '200px' }}
        >
          <Form.Row>{matchedImages}</Form.Row>
        </Form.TabContent>
        <Form.TabContent id="zip" label="Upload ZIP" tabStyle={{ width: '200px' }}>
          <div className={`${this.styles.inputGroup} ${this.styles[styleName]}`}>
            <ZipImageSelector resourceType={ResourceType.BulkImageImport} onUploadSuccess={this.onUploadSuccess} />
            <Form.Row>
              <Form.Fieldset cols={1 / 2}>
                {this.state.matchedImageListItems.length > 0 && (
                  <div>
                    <Form.Legend label="Matched Images" />
                    <div style={{ height: 370, position: 'relative' }}>
                      <ScrollablePane>
                        <DetailsList
                          items={this.state.matchedImageListItems}
                          columns={this.columns}
                          selectionMode={SelectionMode.none}
                          compact={true}
                        />
                      </ScrollablePane>
                    </div>
                  </div>
                )}
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 2}>
                {this.state.unmatchedImageListItems.length > 0 && (
                  <div>
                    <Form.Legend label="Unmatched Images" />
                    <div style={{ height: 370, position: 'relative' }}>
                      <ScrollablePane>
                        <DetailsList
                          items={this.state.unmatchedImageListItems}
                          columns={this.columns}
                          selectionMode={SelectionMode.none}
                          compact={true}
                        />
                      </ScrollablePane>
                    </div>
                  </div>
                )}
              </Form.Fieldset>
            </Form.Row>
          </div>
        </Form.TabContent>
      </Form.TabPanel>
    );
  }
}
BulkImageSelector.defaultProps = {};
